import { ref, watch, type Ref } from 'vue'
import type { ObjectInventoryField } from '@/entities/Inventory/ObjectTypes'
import type { RuntimeFieldValueMap } from '@/features/Inventory/ObjectListEdit/model/runtimeFieldBindings'
import {
  isEmptyMandatoryValue,
  isFieldMandatory,
  type MandatoryValidationResult,
} from './validateMandatoryFields'

/** Tracks which field ids should show mandatory-error styling after a failed save attempt. */
export function useMandatoryFieldHighlight(
  fieldValues: Ref<RuntimeFieldValueMap>,
  fieldMetadata: Ref<ObjectInventoryField[] | undefined>,
) {
  const invalidFieldIds = ref<Set<string>>(new Set())

  function applyMandatoryValidationResult(result: MandatoryValidationResult) {
    if (result.valid) {
      invalidFieldIds.value = new Set()
      return
    }
    invalidFieldIds.value = new Set(result.errors.map((e) => e.fieldId))
  }

  function clearMandatoryFieldHighlight() {
    invalidFieldIds.value = new Set()
  }

  watch(
    fieldValues,
    (vals) => {
      if (invalidFieldIds.value.size === 0) return
      const meta = fieldMetadata.value ?? []
      const metaById = new Map(meta.map((f) => [String(f.id), f]))
      const next = new Set(invalidFieldIds.value)
      let changed = false
      for (const fieldId of invalidFieldIds.value) {
        const def = metaById.get(fieldId)
        if (!isFieldMandatory(def?.mandatory) || !isEmptyMandatoryValue(String(def.type), vals[fieldId])) {
          next.delete(fieldId)
          changed = true
        }
      }
      if (changed) invalidFieldIds.value = next
    },
    { deep: true },
  )

  return {
    invalidFieldIds,
    applyMandatoryValidationResult,
    clearMandatoryFieldHighlight,
  }
}
